import { chakra, Box } from "@chakra-ui/react";
import React from "react";

import type { TokenInfo } from "types/api/token";

import { route } from "nextjs/routes";

import config from "configs/app";
import { useMultichainContext } from "lib/contexts/multichain";
import getChainTooltipText from "lib/multichain/getChainTooltipText";
import getIconUrl from "lib/multichain/getIconUrl";
import { Skeleton } from "toolkit/chakra/skeleton";
import { TruncatedTextTooltip } from "toolkit/components/truncation/TruncatedTextTooltip";
import { Image } from "toolkit/chakra/image";
import { Tooltip } from "toolkit/chakra/tooltip";
import * as EntityBase from "ui/shared/entities/base/components";
import TokenLogoPlaceholder from "ui/shared/TokenLogoPlaceholder";

import { distributeEntityProps, getIconProps } from "../base/utils";

// BGB(old) token 的特殊地址
export const BGB_OLD_ADDRESS = [
  "0x55d1f1879969bdbB9960d269974564C58DBc3238",
  "0x982729d04b67d9d70264d9657946D5bb6D2e9bBD",
];
export const BGB_OLD_SYMBOL = "BGB(old)";
export const BGB_OLD_ICON_URL =
  "https://raw.githubusercontent.com/morph-l2/morph-list/main/tokenIcons/BGB(old).svg";
export const BGB_OLD_NAME_SUFFIX = "(old)";
export const BGB_OLD_NAME = "BitgetToken";
// 真正的 BGB token 地址
export const BGB_ADDRESS = "0x389C08Bc23A7317000a1FD76c7c5B0cb0b4640b5";

// USDT.e (bridged USDT) 的特殊地址
export const USDT_E_ADDRESS = [
  "0xc7D67A9cBB121b3b0b9c053DD9f469523243379A", // morph mainnet
  "0xb646c743B4BA47ac03Bee360BB2484Fb55Db8d7e", // morph hoodi
  "0x517FA2eF448FE76d1a8e9c6a5F2508B6484d978f", // morph qanet
];
export const USDT_E_DISPLAY = "USDT.e";
export const USDT_E_DISPLAY_NAME = "Tether Morph Bridged";
export const USDT_E_ICON_URL =
  "https://raw.githubusercontent.com/morph-l2/morph-list/main/tokenIcons/USDT.svg";

// USDC.e (bridged USDC) 的特殊地址
export const USDC_E_ADDRESS = [
  "0xe34c91815d7fc18a9e2148bcd4241d0a5848b693", // morph mainnet
  "0x1178341838B764dCfFA5BCEAb1d41443Fd71a227", // morph hoodi
  "0xa653309E584ca8195840baE83e7E9829EE7820fA", // morph qanet
];
export const USDC_E_DISPLAY = "USDC.e";
export const USDC_E_DISPLAY_NAME = "USD Coin Morph Bridged";
export const USDC_E_ICON_URL =
  "https://raw.githubusercontent.com/morph-l2/morph-list/main/tokenIcons/USDC.svg";

// USDT0 展示用（Symbol 显示 USDT，Name 显示 Tether USD，Icon 不变）
export const USDT0_DISPLAY_SYMBOL = "USDT";
export const USDT0_DISPLAY_NAME = "Tether USD";

// 检查是否为 BGB(old) token
export const isBgbOldToken = (address?: string | null): boolean => {
  if (!address) return false;
  return BGB_OLD_ADDRESS.some(
    (addr) => addr.toLowerCase() === address.toLowerCase(),
  );
};

// 检查是否为真正的 BGB token
export const isBgbToken = (address?: string | null): boolean => {
  if (!address) return false;
  return address.toLowerCase() === BGB_ADDRESS.toLowerCase();
};

// 检查是否为允许显示图标的 BGB token（真正的 BGB 或 BGB(old)）
export const isAllowedBgbToken = (address?: string | null): boolean => {
  return isBgbToken(address) || isBgbOldToken(address);
};

// 检查是否为 USDT.e token
export const isUsdtEToken = (address?: string | null): boolean => {
  if (!address) return false;
  return USDT_E_ADDRESS.some(
    (addr) => addr.toLowerCase() === address.toLowerCase(),
  );
};

// 检查是否为 USDC.e token
export const isUsdcEToken = (address?: string | null): boolean => {
  if (!address) return false;
  return USDC_E_ADDRESS.some(
    (addr) => addr.toLowerCase() === address.toLowerCase(),
  );
};

// 检查是否为 USDT0 token（按 symbol 判断）
export const isUsdt0Token = (symbol?: string | null): boolean => {
  return symbol?.toUpperCase() === "USDT0";
};

// 获取展示用的 token symbol（对 BGB(old)、USDT.e、USDC.e、USDT0 进行特殊处理）
export const getTokenDisplaySymbol = (
  address: string | null | undefined,
  symbol: string | null | undefined,
): string => {
  if (isUsdtEToken(address)) return USDT_E_DISPLAY;
  if (isUsdcEToken(address)) return USDC_E_DISPLAY;
  if (isUsdt0Token(symbol)) return USDT0_DISPLAY_SYMBOL;
  if (isBgbOldToken(address)) return BGB_OLD_SYMBOL;
  return symbol || "";
};

const stripOldSuffix = (value: string): string =>
  value.replace(/\s*\(old\)\s*$/i, "");

const appendOldSuffix = (value: string): string => {
  const trimmed = value.trim();
  if (/\(old\)\s*$/i.test(trimmed)) {
    return trimmed;
  }
  return `${trimmed}${BGB_OLD_NAME_SUFFIX}`;
};

export const getTokenDisplayName = (
  name: string | null | undefined,
  address: string | null | undefined,
  options?: { omitOldSuffix?: boolean; symbol?: string | null },
): string => {
  if (isUsdtEToken(address)) {
    return USDT_E_DISPLAY_NAME;
  }
  if (isUsdcEToken(address)) {
    return USDC_E_DISPLAY_NAME;
  }
  if (isUsdt0Token(options?.symbol)) {
    return USDT0_DISPLAY_NAME;
  }
  if (!name) {
    return "";
  }
  if (!isBgbOldToken(address)) {
    return name;
  }
  if (options?.omitOldSuffix) {
    return stripOldSuffix(name);
  }
  return appendOldSuffix(stripOldSuffix(name));
};

export const getTokenTransferDisplayName = (
  address: string | null | undefined,
  name: string | null | undefined,
  options?: { omitOldSuffix?: boolean; symbol?: string | null },
): string => {
  if (isUsdtEToken(address)) return USDT_E_DISPLAY_NAME;
  if (isUsdcEToken(address)) return USDC_E_DISPLAY_NAME;
  if (isUsdt0Token(options?.symbol)) return USDT0_DISPLAY_NAME;
  return getTokenDisplayName(name, address, options);
};

type LinkProps = EntityBase.LinkBaseProps & Pick<EntityProps, "token">;

const Link = chakra((props: LinkProps) => {
  const hash = props.token.address_hash || props.token.address;
  const defaultHref = hash
    ? route(
        { pathname: "/token/[hash]", query: { ...props.query, hash } },
        props.chain ? { chain: props.chain } : undefined,
      )
    : undefined;

  return (
    <EntityBase.Link {...props} href={props.href ?? defaultHref}>
      {props.children}
    </EntityBase.Link>
  );
});

type IconProps = Pick<EntityProps, "token" | "className"> &
  EntityBase.IconBaseProps;

const Icon = (props: IconProps) => {
  if (props.noIcon) {
    return null;
  }

  const iconProps = getIconProps(props, Boolean(props.shield ?? props.chain));
  const tokenAddress = props.token.address_hash || props.token.address;

  // 如果 symbol 是 "BGB" 但地址不是允许的 BGB 地址，不显示图标
  if (
    props.token.symbol?.toUpperCase() === "BGB" &&
    !isAllowedBgbToken(tokenAddress)
  ) {
    return <TokenLogoPlaceholder />;
  }

  const iconUrl = isBgbOldToken(tokenAddress)
    ? BGB_OLD_ICON_URL
    : isUsdtEToken(tokenAddress)
      ? USDT_E_ICON_URL
      : isUsdcEToken(tokenAddress)
        ? USDC_E_ICON_URL
        : (props.token.icon_url ?? undefined);

  const styles = {
    marginRight: iconProps.marginRight,
    boxSize: iconProps.boxSize,
    borderRadius: props.token.type === "ERC-20" ? "full" : "base",
  };

  const chainId = config.chain.id;

  // 如果 symbol 是 "BGB" 但地址不是允许的 BGB 地址，不显示图标
  if (
    props.token.symbol?.toUpperCase() === "BGB" &&
    !isAllowedBgbToken(tokenAddress)
  ) {
    return <TokenLogoPlaceholder {...styles} className={props.className} />;
  }

  // 构建回退 URL 列表，按优先级顺序
  const fallbackUrls: Array<string | undefined> = [];

  // 特殊处理 BGB(old)、USDT.e、USDC.e token
  if (isBgbOldToken(tokenAddress)) {
    fallbackUrls.push(BGB_OLD_ICON_URL);
  } else if (isUsdtEToken(tokenAddress)) {
    fallbackUrls.push(USDT_E_ICON_URL);
  } else if (isUsdcEToken(tokenAddress)) {
    fallbackUrls.push(USDC_E_ICON_URL);
  } else {
    // 1. 优先使用 GitHub 图标（如果有 symbol）
    //    URL: https://raw.githubusercontent.com/morph-l2/morph-list/main/tokenIcons/{SYMBOL}.svg
    if (props.token.symbol) {
      fallbackUrls.push(
        `https://raw.githubusercontent.com/morph-l2/morph-list/main/tokenIcons/${props.token.symbol.toLocaleUpperCase()}.svg`,
      );
    }

    // 2. GitHub 加载失败时，使用 Bulbaswap 图标（如果有地址和链 ID）
    //    URL: https://assets.bulbaswap.io/icons/{chainId}/address/{address}.png
    // if (tokenAddress && chainId) {
    //   fallbackUrls.push(`https://assets.bulbaswap.io/icons/${chainId}/address/${tokenAddress.toLowerCase()}.png`);
    // }
  }

  // 3. 如果前两者都失败，使用 icon_url 作为最后的回退
  if (props.token.icon_url) {
    fallbackUrls.push(props.token.icon_url);
  }

  // 使用第一个 URL 作为主要源，其他作为回退
  // 当第一个 URL 加载失败时，IconWithFallbacks 会自动尝试下一个 URL
  const primaryUrl = fallbackUrls[0];
  const remainingFallbacks = fallbackUrls.slice(1);

  return (
    <IconWithFallbacks
      {...styles}
      className={props.className}
      primaryUrl={primaryUrl}
      fallbackUrls={remainingFallbacks}
      finalFallback={<TokenLogoPlaceholder />}
      // src={ iconUrl }
      alt={`${getTokenDisplayName(props.token.name, tokenAddress, { symbol: props.token.symbol }) || "token"} logo`}
      // fallback={ <TokenLogoPlaceholder/> }
      shield={
        props.shield ??
        (props.chain ? { src: getIconUrl(props.chain) } : undefined)
      }
      hint={
        props.chain ? getChainTooltipText(props.chain, "Token on ") : undefined
      }
      isLoading={props.isLoading}
    />
  );
};

// 支持多级回退的 Icon 组件
type IconWithFallbacksProps = {
  className?: string;
  isLoading?: boolean;
  marginRight?: unknown;
  boxSize?: unknown;
  borderRadius?: string;
  shield?: EntityBase.IconBaseProps["shield"];
  hint?: string;
  primaryUrl?: string;
  fallbackUrls?: Array<string | undefined>;
  finalFallback?: React.ReactNode;
  alt?: string;
};

const IconWithFallbacks = React.memo((props: IconWithFallbacksProps) => {
  const {
    primaryUrl,
    fallbackUrls = [],
    finalFallback,
    isLoading,
    marginRight,
    boxSize,
    borderRadius,
    shield,
    hint,
    alt,
    className,
  } = props;
  const [currentUrlIndex, setCurrentUrlIndex] = React.useState(0);
  const [hasError, setHasError] = React.useState(false);

  const allUrls = React.useMemo(() => {
    return [primaryUrl, ...fallbackUrls].filter(Boolean) as string[];
  }, [primaryUrl, fallbackUrls]);

  // 当 URL 列表改变时，重置索引和错误状态
  React.useEffect(() => {
    setCurrentUrlIndex(0);
    setHasError(false);
  }, [primaryUrl, fallbackUrls]);

  const currentUrl = allUrls[currentUrlIndex];

  const handleError = React.useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      // 使用函数式更新确保获取最新的 allUrls 长度
      setCurrentUrlIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;
        if (nextIndex < allUrls.length) {
          setHasError(false); // 重置错误状态以尝试下一个 URL
          return nextIndex;
        } else {
          // 所有 URL 都失败了，显示最终回退
          setHasError(true);
          return prevIndex;
        }
      });
    },
    [allUrls.length],
  );

  // 当 URL 索引改变时，重置错误状态
  React.useEffect(() => {
    setHasError(false);
  }, [currentUrlIndex]);

  const commonProps = {
    marginRight: marginRight ?? 2,
    boxSize: boxSize ?? 6,
    borderRadius: borderRadius ?? "base",
    flexShrink: 0,
    minW: 0,
    className,
  };

  if (isLoading) {
    return <Skeleton loading {...commonProps} />;
  }

  // 如果没有 URL 或所有 URL 都失败了，显示最终回退（默认占位符）
  if (!currentUrl || (hasError && currentUrlIndex >= allUrls.length - 1)) {
    if (React.isValidElement(finalFallback)) {
      return React.cloneElement(finalFallback, commonProps);
    }
    return finalFallback || <TokenLogoPlaceholder {...commonProps} />;
  }

  // 设置 fallback 为占位符，确保加载失败时显示默认图标
  const imageElement = (
    <Image
      {...commonProps}
      src={currentUrl}
      alt={alt || "token logo"}
      onError={handleError}
      fallback={
        React.isValidElement(finalFallback)
          ? React.cloneElement(finalFallback, commonProps)
          : finalFallback || <TokenLogoPlaceholder {...commonProps} />
      }
    />
  );

  const imageWithHint = hint ? (
    <Tooltip content={hint}>{imageElement}</Tooltip>
  ) : (
    imageElement
  );

  if (!shield) {
    return imageWithHint;
  }

  return (
    <Box
      position="relative"
      display="inline-flex"
      alignItems="center"
      flexShrink={0}
    >
      {imageWithHint}
      <EntityBase.IconShield {...shield} />
    </Box>
  );
});

IconWithFallbacks.displayName = "IconWithFallbacks";

type ContentProps = Omit<EntityBase.ContentBaseProps, "text"> &
  Pick<EntityProps, "token" | "jointSymbol" | "onlySymbol" | "noSymbol">;

const Content = chakra((props: ContentProps) => {
  const tokenAddress = props.token.address_hash || props.token.address;
  const displaySymbol = getTokenDisplaySymbol(tokenAddress, props.token.symbol);
  const shouldOmitOldInName = Boolean(
    displaySymbol &&
    !props.onlySymbol &&
    (props.jointSymbol || !props.noSymbol),
  );
  const displayNameWithOld = getTokenDisplayName(
    props.token.name,
    tokenAddress,
    { symbol: props.token.symbol },
  );
  const displayNameWithoutOld = getTokenDisplayName(
    props.token.name,
    tokenAddress,
    { omitOldSuffix: true, symbol: props.token.symbol },
  );

  const nameString = [
    !props.onlySymbol &&
      (shouldOmitOldInName ? displayNameWithoutOld : displayNameWithOld),
    props.onlySymbol && (displaySymbol ?? displayNameWithOld),
    displaySymbol &&
      props.jointSymbol &&
      !props.onlySymbol &&
      `(${displaySymbol})`,
  ]
    .filter(Boolean)
    .join(" ");

  return <EntityBase.Content {...props} text={nameString} truncation="tail" />;
});

type SymbolProps = Pick<
  EntityProps,
  "token" | "isLoading" | "noSymbol" | "jointSymbol" | "onlySymbol"
>;

const Symbol = (props: SymbolProps) => {
  const tokenAddress = props.token.address_hash || props.token.address;
  const symbol = getTokenDisplaySymbol(tokenAddress, props.token.symbol);

  if (!symbol || props.noSymbol || props.jointSymbol || props.onlySymbol) {
    return null;
  }

  return (
    <Skeleton
      loading={props.isLoading}
      display="inline-flex"
      alignItems="center"
      maxW="20%"
      ml={2}
      color="text.secondary"
    >
      <div>(</div>
      <TruncatedTextTooltip label={symbol}>
        <chakra.span
          display="inline-block"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          height="fit-content"
        >
          {symbol}
        </chakra.span>
      </TruncatedTextTooltip>
      <div>)</div>
    </Skeleton>
  );
};

type CopyProps = Omit<EntityBase.CopyBaseProps, "text"> &
  Pick<EntityProps, "token">;

const Copy = (props: CopyProps) => {
  return (
    <EntityBase.Copy
      {...props}
      text={props.token.address_hash ?? props.token.address ?? ""}
    />
  );
};

const Container = EntityBase.Container;

export interface EntityProps extends EntityBase.EntityBaseProps {
  token: Pick<
    TokenInfo,
    "address" | "address_hash" | "icon_url" | "name" | "symbol" | "type"
  >;
  noSymbol?: boolean;
  jointSymbol?: boolean;
  onlySymbol?: boolean;
  preserveName?: boolean;
}

const TokenEntity = (props: EntityProps) => {
  const multichainContext = useMultichainContext();
  const partsProps = distributeEntityProps(props, multichainContext);

  const content = <Content {...partsProps.content} />;

  return (
    <Container w="100%" {...partsProps.container}>
      <Icon {...partsProps.icon} />
      {props.noLink ? content : <Link {...partsProps.link}>{content}</Link>}
      <Symbol {...partsProps.symbol} />
      <Copy {...partsProps.copy} />
    </Container>
  );
};

export default React.memo(chakra(TokenEntity));

export { Container, Link, Icon, Content, Copy };
